/**
 * Central icon registry.
 *
 * The dashboard renders stroke-based lucide icons that inherit `currentColor`, so an icon takes
 * the color of whatever surface it sits on. Color is applied only where it already carries
 * meaning (incident type, unit status, task-set color, per-point color).
 *
 * Icons are *persisted data*, not just presentation: `map_points.icon`, `sensors.icon`,
 * `task_sets.icon` and `field_units.icon` store strings, and older rows store emoji. Rather than
 * migrating the database, `resolveIcon()` maps legacy emoji and legacy marker data-URIs to icon
 * keys at render time. Existing rows are never rewritten.
 */
import {
  Activity, Anchor, Antenna, ArrowRight, ArrowUp, Ban, Brain, Building2, Camera, Car, Cctv,
  Check, ChevronDown, ChevronLeft, ChevronRight, CircleHelp, CircleStop, Clapperboard,
  ClipboardList, Cloud, Compass, Cone, Cpu, Cross, Dam, DoorOpen, Dot, Download, Drone, Droplet,
  Droplets, ExternalLink, Eye, Factory, Fence, FileSpreadsheet, Flag, Flame, Fuel, Gauge, Grid3x3,
  Helicopter, Hexagon, Hourglass, House,
  Images, LandPlot, Landmark, Layers, LayoutGrid, LogOut, Map as MapIcon, MapPin, Maximize, Maximize2, Mic,
  Minimize, Minimize2, MousePointer2, Navigation2, Pencil, Pentagon, Plane, Play, Power, Radio, RadioTower, Radar,
  RefreshCw, RotateCcw, Route, Ruler, ScanEye, Search, Share2, Ship, Shield, Smartphone, Spline,
  Square, Stamp, Star, StickyNote, Tag, Target, Thermometer, Tornado, TreePine, TriangleAlert,
  Truck, Trash2, Type, Undo2, Users, Video, Waves, Waypoints, Wind, X, Zap,
  type LucideIcon,
} from 'lucide-react';

export type IconKey =
  // sensor types
  | 'camera' | 'mic' | 'seismic' | 'sonar' | 'phone' | 'radar' | 'occupied'
  // field unit types
  | 'post' | 'vehicle' | 'firetruck'
  // incident types
  | 'threat' | 'medical' | 'infra' | 'environ' | 'unknown'
  // live map layers. `heading` is north-pointing and safe under `rotate(track)`;
  // `aircraft` is a side view for static headers only.
  | 'drone' | 'heading' | 'aircraft' | 'vessel' | 'webcam' | 'fov'
  // map entities
  | 'site' | 'photosite' | 'area' | 'path' | 'sensor' | 'taskset' | 'mission' | 'parcel'
  // marker picker choices
  | 'house' | 'tree' | 'flag' | 'star' | 'droplet' | 'hive' | 'warning'
  | 'factory' | 'antenna' | 'ship' | 'fire' | 'buildings' | 'landmark' | 'gate'
  // hydrology + flood defence — scripts/import_thessaly_hydrology.py writes these
  | 'water' | 'dam' | 'levee' | 'pump' | 'bridge' | 'gauge'
  // U-space ground picture — scripts/import_uspace_attica.py writes these
  | 'vertiport' | 'corridor'
  // UI chrome
  | 'weather' | 'wind' | 'temp' | 'cloud' | 'visibility'
  | 'fleet' | 'edge' | 'mqtt' | 'oplog' | 'captures' | 'models' | 'surveillance' | 'analytics' | 'assistant' | 'admin' | 'logout'
  | 'import-excel' | 'import-dcim' | 'upload' | 'download' | 'spinner'
  | 'select' | 'rect' | 'polygon' | 'arrow' | 'freehand' | 'text'
  | 'north' | 'compass' | 'annotate' | 'done' | 'notes' | 'tags' | 'undo' | 'delete' | 'clear'
  | 'detect' | 'roadseg' | 'blur' | 'geomask' | 'stop' | 'power'
  | 'share' | 'extlink' | 'refresh' | 'search' | 'edit' | 'play' | 'dot'
  | 'streams' | 'matrix' | 'mgrs' | 'uspace' | 'clearance' | 'hall' | 'worldmap'
  | 'expand' | 'collapse' | 'reset' | 'filter-off' | 'snapshot'
  // the browser Fullscreen API — distinct from expand/collapse, which pop the
  // video panel out to a floating window and back
  | 'fullscreen' | 'exit-fs'
  | 'chev-left' | 'chev-right' | 'chev-down';

export const ICONS: Record<IconKey, LucideIcon> = {
  // sensor types
  camera: Cctv,
  mic: Mic,
  seismic: Activity,
  analytics: Activity,
  sonar: Waves,
  phone: Smartphone,
  radar: Radar,
  occupied: Users,

  // field unit types
  post: Shield,
  vehicle: Car,
  firetruck: Truck,

  // incident types
  threat: TriangleAlert,
  medical: Cross,
  infra: Zap,
  environ: Tornado,
  unknown: CircleHelp,

  // live map layers
  drone: Drone,
  heading: Navigation2,
  aircraft: Plane,
  vessel: Ship,
  webcam: Video,
  fov: Cone,

  // map entities
  site: MapPin,
  photosite: Camera,
  area: Hexagon,
  path: Ruler,
  sensor: RadioTower,
  taskset: ClipboardList,
  mission: Target,
  parcel: LandPlot,

  // marker picker choices
  house: House,
  tree: TreePine,
  flag: Flag,
  star: Star,
  droplet: Droplet,
  hive: Hexagon,
  warning: TriangleAlert,
  factory: Factory,
  antenna: RadioTower,
  ship: Anchor,
  fire: Flame,
  buildings: Building2,
  landmark: Landmark,
  gate: DoorOpen,

  // hydrology + flood defence
  water: Droplets,
  dam: Dam,
  levee: Fence,
  pump: Fuel,
  bridge: Waypoints,
  gauge: Gauge,

  // U-space ground picture
  vertiport: Helicopter,
  corridor: Route,

  // UI chrome
  weather: Cloud,
  wind: Wind,
  temp: Thermometer,
  cloud: Cloud,
  visibility: Eye,
  fleet: Drone,
  edge: Cpu,
  mqtt: Antenna,
  oplog: ClipboardList,
  captures: Clapperboard,
  models: Brain,
  surveillance: Shield,
  assistant: Brain,
  admin: Users,
  logout: LogOut,
  'import-excel': FileSpreadsheet,
  'import-dcim': Images,
  upload: Images,
  download: Download,
  spinner: Hourglass,
  select: MousePointer2,
  rect: Square,
  polygon: Pentagon,
  arrow: ArrowRight,
  freehand: Spline,
  text: Type,
  north: ArrowUp,
  compass: Compass,
  annotate: Pencil,
  done: Check,
  notes: StickyNote,
  tags: Tag,
  undo: Undo2,
  delete: Trash2,
  clear: X,
  detect: Target,
  roadseg: Route,
  blur: ScanEye,
  geomask: Layers,
  stop: CircleStop,
  power: Power,
  share: Share2,
  extlink: ExternalLink,
  refresh: RefreshCw,
  search: Search,
  edit: Pencil,
  play: Play,
  dot: Dot,
  streams: Radio,
  matrix: LayoutGrid,
  mgrs: Grid3x3,
  uspace: Hexagon,
  clearance: Stamp,
  hall: Landmark,
  worldmap: MapIcon,
  expand: Maximize2,
  snapshot: Camera,
  collapse: Minimize2,
  fullscreen: Maximize,
  'exit-fs': Minimize,
  reset: RotateCcw,
  'filter-off': Ban,
  'chev-left': ChevronLeft,
  'chev-right': ChevronRight,
  'chev-down': ChevronDown,
};

const ICON_KEYS = new Set<string>(Object.keys(ICONS));

export function isIconKey(v: unknown): v is IconKey {
  return typeof v === 'string' && ICON_KEYS.has(v);
}

/**
 * Keys the frontend may write to an `icon` column.
 *
 * `backend/main.py` declares `icon: Optional[str] = Field(..., max_length=10)` on the point,
 * sensor and task-set models, so every key here must be 10 characters or fewer — a longer key
 * would 422 on create, and `App.tsx` swallows that error. Enforced by `registry.test.ts`.
 *
 * `metadata.marker_icon` is exempt: metadata is JSONB with no length constraint.
 */
export const PERSISTABLE_KEYS: IconKey[] = [
  'site', 'photosite', 'area', 'path', 'sensor', 'taskset', 'mission', 'parcel',
  'camera', 'mic', 'seismic', 'sonar', 'phone', 'radar',
  'post', 'vehicle', 'firetruck',
  // industrial site layout — scripts/import_refinery_layout.py writes these keys
  // directly rather than emoji, so they need no LEGACY_EMOJI entry.
  'factory', 'gate', 'buildings', 'antenna',
  // Karditsa hydrology — scripts/import_thessaly_hydrology.py + seed_flood()
  'water', 'dam', 'levee', 'pump', 'bridge', 'gauge',
  // Attica U-space — scripts/import_uspace_attica.py + seed_uspace().
  // `medical` doubles as an incident type; the importer writes it to map_points.icon so
  // hospital transfer destinations read as a cross, not as a vertiport helicopter.
  'vertiport', 'corridor', 'drone', 'medical',
  // Μεγίστη island garrison — scripts/import_megisti_layout.py + seed_garrison().
  // All five already existed in ICONS; they were simply never persistable. `ship` is the
  // anchor (a quay), `vessel` the hull (a patrol boat) — the importer and the seeder use
  // them for different things, so do not collapse them.
  'flag', 'aircraft', 'ship', 'vessel', 'fov', 'landmark',
];

/**
 * Emoji written by older frontend builds, by the backend Pydantic/DDL defaults
 * (`backend/main.py`), and by the seed/import scripts (`scripts/seed_demo_scenarios.py`,
 * `scripts/import_deth_layout.py`, `scripts/import_sites.py`).
 *
 * A missing entry degrades silently to the `site` fallback rather than erroring, so this map must
 * stay in sync with anything that writes an icon column.
 */
const LEGACY_EMOJI: Record<string, IconKey> = {
  // sensor types (App.tsx SENSOR_TYPES, sensors table default)
  '\u{1F4F7}': 'camera',        // 📷
  '\u{1F3A4}': 'mic',           // 🎤 — also the ΔΕΘ congress-centre marker
  '\u{1F30B}': 'seismic',       // 🌋
  '\u{1F30A}': 'sonar',         // 🌊 — also the seed water-level gauges
  '\u{1F4F1}': 'phone',         // 📱

  // field units (unitsTypes.ts UNIT_TYPES)
  '\u{1F6E1}\u{FE0F}': 'post',  // 🛡️ with variation selector
  '\u{1F6E1}': 'post',          // 🛡 without
  '\u{1F693}': 'vehicle',       // 🚓
  '\u{1F694}': 'vehicle',       // 🚔 — seed staging area
  '\u{1F692}': 'firetruck',     // 🚒 — seed fire appliances

  // map entities (map_points / task_sets defaults)
  '\u{1F4CD}': 'site',          // 📍
  '\u{1F4F8}': 'photosite',     // 📸 — DCIM sentinel, display only (see note below)
  '\u{1F4CB}': 'taskset',       // 📋
  '⬡': 'area',             // ⬡
  '\u{1F4CF}': 'path',          // 📏
  '〰\u{FE0F}': 'path',     // 〰️
  '\u{1F4E1}': 'antenna',       // 📡 — sensor tool + seed telecom mast

  // seeded scenario points
  '\u{1F3E0}': 'house',         // 🏠
  '\u{1F3D8}\u{FE0F}': 'house', // 🏘️ — KAEK demo task set
  '\u{1F3D8}': 'house',         // 🏘
  '\u{1F333}': 'tree',          // 🌳
  '\u{1F525}': 'fire',          // 🔥 — FIRMS hotspots
  '\u{1F3AF}': 'mission',       // 🎯 — quake epicenter
  '⚡': 'infra',            // ⚡ — damaged substation

  // ΔΕΘ layout import (scripts/import_deth_layout.py)
  '\u{1F3D9}': 'buildings',     // 🏙 site perimeter
  '\u{1F3DB}': 'landmark',      // 🏛 pavilion
  '\u{1F6AA}': 'gate',          // 🚪 access gate

  // incident glyphs
  '⚠\u{FE0F}': 'threat',   // ⚠️
  '⚠': 'threat',           // ⚠
  '\u{1F32A}\u{FE0F}': 'environ', // 🌪️
  '\u{1F32A}': 'environ',       // 🌪

  // live layers
  '\u{1F681}': 'drone',         // 🚁
  '✈\u{FE0F}': 'aircraft', // ✈️
  '✈': 'aircraft',         // ✈
  '\u{1F4F9}': 'webcam',        // 📹
};

/**
 * The marker images offered before this change. The `url` strings are kept verbatim because
 * `map_points.metadata.marker_image` stores the whole data URI, and lookups must match the stored
 * value byte-for-byte (they were produced by `encodeURIComponent`).
 *
 * Nothing renders these any more — they exist only so old rows resolve to the right icon.
 */
const LEGACY_MARKER_IMAGES: { key: IconKey; url: string }[] = [
  { key: 'hive', url: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><path fill="#F5A623" d="M32 4l20 12v24L32 52 12 40V16z"/><path fill="#D4820E" d="M32 4l20 12-20 12L12 16zm0 24l20 12-20 12L12 40z"/><path fill="#FFF" opacity=".3" d="M32 4l20 12-20 12z"/><circle cx="26" cy="28" r="2" fill="#333"/><circle cx="38" cy="28" r="2" fill="#333"/></svg>') },
  { key: 'house', url: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><path fill="#4A90D9" d="M32 8L8 28h8v24h32V28h8z"/><path fill="#2C5F8A" d="M32 8L8 28h8l16-14 16 14h8z"/><rect fill="#FFF" x="22" y="32" width="8" height="8" rx="1"/><rect fill="#FFF" x="34" y="32" width="8" height="8" rx="1"/><rect fill="#8B5E3C" x="28" y="40" width="8" height="12"/></svg>') },
  { key: 'tree', url: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><path fill="#2ECC40" d="M32 4C20 4 14 16 14 24c0 10 8 18 18 18s18-8 18-18c0-8-6-20-18-20z"/><rect fill="#8B5E3C" x="28" y="40" width="8" height="20" rx="2"/><path fill="#1A9928" d="M32 4c-6 0-12 6-14 14 4-4 10-6 14-6s10 2 14 6C44 10 38 4 32 4z"/></svg>') },
  { key: 'site', url: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><path fill="#E74C3C" d="M32 2C20.95 2 12 10.95 12 22c0 14 20 38 20 38s20-24 20-38C52 10.95 43.05 2 32 2z"/><circle fill="#FFF" cx="32" cy="22" r="8"/></svg>') },
  { key: 'flag', url: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect fill="#555" x="10" y="8" width="3" height="50" rx="1.5"/><path fill="#E74C3C" d="M13 8h38l-8 12 8 12H13z"/></svg>') },
  { key: 'droplet', url: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><path fill="#3498DB" d="M32 4S16 24 16 38c0 8.84 7.16 16 16 16s16-7.16 16-16C48 24 32 4 32 4z"/><path fill="#FFF" opacity=".3" d="M32 4S20 20 18 34c6-4 14-4 20 0 4-6 8-10 8-10S38 12 32 4z"/></svg>') },
  { key: 'warning', url: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><path fill="#F39C12" d="M32 4L2 58h60z"/><path fill="#FFF" d="M29 24h6v18h-6zm0 22h6v6h-6z"/></svg>') },
  { key: 'star', url: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><path fill="#F1C40F" d="M32 4l7 22h23l-18.5 13.5L50 62 32 48.5 14 62l6.5-22.5L2 26h23z"/></svg>') },
];

/**
 * Both lookups are Maps, not plain objects: `point.icon` can hold an arbitrary user-supplied
 * string (App.tsx copies a CSV column straight into it), and an object lookup for a name like
 * `hasOwnProperty` would return the inherited function rather than undefined.
 */
const LEGACY_EMOJI_MAP = new Map<string, IconKey>(Object.entries(LEGACY_EMOJI));

const LEGACY_MARKER_URL = new Map<string, IconKey>(
  LEGACY_MARKER_IMAGES.map(m => [m.url, m.key] as const),
);

/** Options offered by the point marker picker. `null` means "use the point's own icon". */
export const MARKER_CHOICES: { key: IconKey | null; label: string }[] = [
  { key: null, label: 'Default' },
  { key: 'site', label: 'Pin' },
  { key: 'house', label: 'House' },
  { key: 'buildings', label: 'Buildings' },
  { key: 'landmark', label: 'Landmark' },
  { key: 'factory', label: 'Factory' },
  { key: 'tree', label: 'Tree' },
  { key: 'hive', label: 'Beehive' },
  { key: 'droplet', label: 'Water' },
  { key: 'flag', label: 'Flag' },
  { key: 'star', label: 'Star' },
  { key: 'warning', label: 'Warning' },
  { key: 'gate', label: 'Gate' },
  { key: 'antenna', label: 'Antenna' },
  { key: 'vehicle', label: 'Vehicle' },
  { key: 'ship', label: 'Anchorage' },
];

/**
 * Resolve a stored icon string to an icon key, for display only.
 *
 * Order: already-a-key -> legacy emoji -> legacy marker data-URI -> `site` fallback.
 * Callers must not write the result back to the database.
 */
export function resolveIcon(stored?: string | null): IconKey {
  if (!stored) return 'site';
  if (ICON_KEYS.has(stored)) return stored as IconKey;
  const emoji = LEGACY_EMOJI_MAP.get(stored);
  if (emoji) return emoji;
  const marker = LEGACY_MARKER_URL.get(stored);
  if (marker) return marker;
  return 'site';
}

/**
 * The marker override a point carries, or null if it has none and falls back to its icon column.
 * Used to highlight the selected swatch in the marker picker, including for legacy rows whose
 * override is still stored as a data URI.
 */
export function markerOverride(
  metadata: { marker_icon?: string; marker_image?: string } | null | undefined,
): IconKey | null {
  if (metadata?.marker_icon && ICON_KEYS.has(metadata.marker_icon)) {
    return metadata.marker_icon as IconKey;
  }
  if (metadata?.marker_image) return LEGACY_MARKER_URL.get(metadata.marker_image) ?? null;
  return null;
}

/**
 * A custom marker image to render as-is, or null.
 *
 * CSV import copies a free-form `metadata` column onto the point (see `handleCsvImport`), so a
 * `marker_image` may hold a URL we have no icon for. Those still render as an <img> rather than
 * silently falling back to a generic pin.
 */
export function customMarkerImage(
  metadata: { marker_icon?: string; marker_image?: string } | null | undefined,
): string | null {
  const src = metadata?.marker_image;
  if (!src || LEGACY_MARKER_URL.has(src)) return null;
  return /^(data:|https?:|\/)/.test(src) ? src : null;
}

/** Resolve a point's marker: explicit `marker_icon`, then a legacy `marker_image`, then its icon. */
export function resolvePointIcon(
  metadata: { marker_icon?: string; marker_image?: string } | null | undefined,
  icon?: string | null,
): IconKey {
  if (metadata?.marker_icon && ICON_KEYS.has(metadata.marker_icon)) {
    return metadata.marker_icon as IconKey;
  }
  if (metadata?.marker_image) {
    const legacy = LEGACY_MARKER_URL.get(metadata.marker_image);
    if (legacy) return legacy;
  }
  return resolveIcon(icon);
}
